import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Button,
  Input,
  Checkbox,
  Message,
  Modal,
} from '@bijie/react-bnb';
import { getErrorCode } from './enum';
import { purchase, fetchUserLeftQuota } from '../../actions/actionsSet/lending';
import { getLabel } from '../../utils/i18n';
import { safeMultiply } from '../../utils/number';
import { track, sensorEventTypes } from '../../utils/sensors';

const NUMBER_ONLY_REGREX = /^[1-9]\d*$/;

const Container = styled.div`
  width: 340px;
  color: #848E9C;
  font-family: 'DINPro-Medium', 'IBMPlexSans-Regular';
  font-size: 14px;
  .title {
    color: #212833;
    padding-bottom: 20px;
    line-height: 26px;
    font-size: 20px;
    font-family: 'DINPro-Medium', 'IBMPlexSans-SemiBold';
  }
  .line {
    position: relative;
    margin-bottom: 5px;
    line-height: 24px;
    &.amount {
      margin: 20px 0;
    }
    .bnc-input {
      font-family: inherit !important;
    }
  }
  .right {
    float: right;
    color: #212833;
  }
  .total-cost {
    font-size: 24px;
  }
  .tip-box {
    position: absolute;
    right: 0;
    bottom: 5px;
    cursor: pointer;
    .max-amount {
      color: #F0B90B;
    }
  }
  .checkbox-box {
    display: inline-block;
    margin-top: 15px;
  }
  .btn-box {
    margin-top: 30px;
    .bnc-btn { width: 100% }
  }
  .error {
    position: absolute;
    left: 0;
    top: 0;
    line-height: 12px;
    color: #F23051;
    font-size: 12px;
  }
  .primary {
    color: #F0B90B;
    &:hover {
      text-decoration: underline;
    }
  }
  .loading {
    width: 26px;
    height: 26px;
    margin-right: 5px;
  }
`;

const SuccessBox = styled.div`
  text-align: center;
  .title {
    margin-bottom: 20px;
    line-height: 26px;
    font-size: 20px;
  }
  .content {
    font-family: 'DINPro', 'IBMPlexSans-Regular';
  }
`;

type Props = {
  i18n: Object,
  project: Object,
  assetCount: String,
  closeSelf: Function,
  callback: Function
}
const PurchasePanel = ({
  i18n, project, closeSelf, assetCount, callback,
}: Props) => {
  const [lot, setLot] = useState();
  const [agree, setAgree] = useState(false);
  const [error, setError] = useState('');
  const [leftQuote, setLeftQuote] = useState(-1);
  const [isFetching, setFetching] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const quote = await fetchUserLeftQuota(project.projectId);
      setLeftQuote(quote);
    }
    fetchData();
  }, []);

  const {
    asset,
    lotSize,
    endTime,
    startTime,
    projectId,
    lotsUpLimit,
    lotsPurchased,
    interestPerLot,
    maxLotsPerUser,
  } = project;
  const totalCoupon = safeMultiply(lot || '0', interestPerLot);
  const totalCost = safeMultiply(lot || '0', lotSize);
  const upLotTotal = safeMultiply(maxLotsPerUser || '0', lotSize);
  let maxinum = Math.min(
    maxLotsPerUser,
    lotsUpLimit - lotsPurchased,
    Math.floor(assetCount / lotSize),
  );
  leftQuote > -1 && (maxinum = Math.min(leftQuote, maxinum));

  const validateLot = () => lot > 0 && lot <= maxinum;
  const handleLotChange = value => {
    if ((!NUMBER_ONLY_REGREX.test(value) && value.length !== 0) || value > maxinum) {
      return undefined;
    }
    return setLot(`${value}`);
  };
  const handlePurchase = async () => {
    let errorKey = '';
    if (!agree) {
      errorKey = 'exchange-lending-checkerror';
    } else if (!validateLot()) {
      errorKey = 'exchange-lending-zeroerror';
    }
    setError(errorKey);
    if (errorKey) return;
    setFetching(true);
    track(sensorEventTypes.LENDING_SUBSCRIBE_SUBMIT, {
      project_id: projectId,
    });
    const [success, { code, failedReason, purchaseRecordId }] = await purchase({
      lot,
      projectId,
    });
    setFetching(false);
    // close modal
    closeSelf();

    if (success) {
      callback();
      Modal.info({
        id: 'purchase-success',
        icon: null,
        closable: true,
        content: (
          <SuccessBox>
            <div className="title">{getLabel('exchange-lending-purchasetitle', i18n)}</div>
            <div className="content">{getLabel('exchange-lending-purchasetip', i18n)}</div>
          </SuccessBox>
        ),
        okText: getLabel('exchange-lending-close', i18n),
      });
    } else {
      Message.fail(getLabel(getErrorCode(code), i18n));
    }
    track(sensorEventTypes.LENDING_SUBSCRIBE_RESULT, {
      project_id: projectId,
      status: success ? 'success' : 'fail',
      error_log: failedReason,
      purchase_record_id: purchaseRecordId,
    });
  };

  return (
    <Container>
      <div className="title">{getLabel(`exchange-lending-${projectId}`, i18n)}</div>
      <div className="line"><span>{getLabel('exchange-lending-lotsize', i18n)}:</span><span className="right">{lotSize} {asset}</span></div>
      <div className="line"><span>{getLabel('exchange-lending-lot', i18n)}:</span><span className="right">{interestPerLot} {asset}</span></div>
      <div className="line">
        <span>{getLabel('exchange-lending-cap', i18n)}:</span>
        <span className="right">{maxLotsPerUser} {getLabel('exchange-lending-lotbase', i18n)} ({upLotTotal} {asset})</span>
      </div>
      <div className="line"><span>{getLabel('exchange-lending-vdate', i18n)}:</span><span className="right">{startTime}</span></div>
      <div className="line"><span>{getLabel('exchange-lending-rdate', i18n)}:</span><span className="right">{endTime}</span></div>
      <div className="line amount">
        <Input
          value={lot}
          style={{ width: '100%' }}
          clear={false}
          placeholder={getLabel('exchange-lending-lotamount', i18n)}
          onChange={handleLotChange}
        />
        <div className="tip-box" onClick={() => setLot(`${maxinum}`)}>
          <span>{getLabel('exchange-lending-Maxinum', i18n)}: </span>
          <span className="max-amount">{maxinum}</span>
        </div>
      </div>
      <div className="line">
        <span>{getLabel('exchange-lending-ecoupon', i18n)}:</span><span className="right">{totalCoupon}</span>
      </div>
      <div className="line">
        <span>{getLabel('exchange-lending-tcost', i18n)}:</span>
        <span className="right"><span className="total-cost">{totalCost}</span> {asset}</span>
      </div>
      <Checkbox checked={agree} onChange={s => setAgree(s)} className="checkbox-box">
        {/* eslint-disable-next-line */}
        <span dangerouslySetInnerHTML={{ __html: getLabel('exchange-lending-ptip', i18n) }} />
      </Checkbox>
      <div className="line"><span className="error">{getLabel(error, i18n)}</span></div>
      <div className="btn-box">
        {
      isFetching
        ? (
          <Button disabled={true} size="lg">
            <img src="../../../static/images/blog/loading.gif" alt="x" className="loading" />
            {getLabel('exchange-common-processing', i18n)}
          </Button>
        )
        : <Button disabled={maxinum === 0} type="primary" size="lg" onClick={handlePurchase}>{getLabel('exchange-lending-purchase', i18n)}</Button>
    }

      </div>
    </Container>
  );
};
PurchasePanel.defaultProps = {
  assetCount: 0,
};

export default PurchasePanel;
